import { makeStyles } from "@mui/styles";
import { Grid, IconButton, Input, TableCell, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import CachedIcon from '@mui/icons-material/Cached';
import { THEME } from "../../../config";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Slider from "./Slider";
import { useSnackbar } from "notistack";
import StyledTooltip from "../../../components/Misc/StyledTooltip";

const useStyles = makeStyles(() => ({
    title: {
        fontWeight: 500,
        fontSize: "1rem",
    },
    titleDisabled: {
        opacity: "50%",
        fontWeight: 500,
        fontSize: "1rem",
    },
    iconButton: {
        opacity: "100%"
    },
    iconButtonDisabled: {
        opacity: "50%"
    },
    iconInfo: {
        marginLeft: "10px",
        position: "relative",
        top: "5px",
        color: THEME.palette.primary.main
    },
    tableCell: {
        paddingRight: 0,
        paddingLeft: "inherit",
    },
    gridSlider: {
        paddingRight: "5%"
    },
    slider: {
        markLabel: {
            color: "red"
        }
    },
    typographyNoWarning: {
        color: THEME.palette.success.main
    },
    typographyAlwaysWarning: {
        color: THEME.palette.error.main
    }
}))

const isSliderArray = (option: ISliderInterface | [ISliderInterface | undefined, ISliderInterface | undefined] | undefined): option is [ISliderInterface | undefined, ISliderInterface | undefined] => {
    return Array.isArray(option);
};

export interface ISliderInterface {
    key: string,
    value: number
}

export interface ISliderCell<T> {
    title: string,
    info?: string,
    option: T | undefined,
    defaultOption: T | undefined,
    setOption: (op: T, basic?: boolean) => void,
    valueLabel?: (value: number) => string,
    active: boolean,
    minValue: number,
    maxValue: number,
    lockMaxValue?: boolean,
    lockMinValue?: boolean,
    lockFirstArrayNumber?: number,
    steps?: number,
    basic?: boolean,
    colspan?: number,
    noWarningMsg?: string,
    alwaysWarningMsg?: string,
    badValueRange?: [number, number],
    dontShowWarningMsg?: boolean,
    triggerOnFirstArrayValueChange?: () => void,
    type: "raising" | "falling" | "static" | "infinite" | "raisingMono" | "fallingMono" | "range"
}

function SliderCell<T extends ISliderInterface | [ISliderInterface | undefined, ISliderInterface | undefined] | undefined>(
  props: ISliderCell<T>
): JSX.Element {
        const classes = useStyles();
        const { enqueueSnackbar } = useSnackbar();
        const castValue = isSliderArray(props.option)
            ? (props.option[0]?.value && props.option[1]?.value) ? [props.option[0]?.value, props.option[1]?.value] : [0, 0]
            : props.option?.value ?? 0;

        const [value, setValue] = useState(castValue);
        const [inputValue, setInputValue] = useState(castValue);

        useEffect(() => {
            if (isSliderArray(props.option)) {
                if ((value as number[])[0] !== props.option[0]?.value && (value as number[])[1] !== props.option[1]?.value) {
                    setValue(castValue)
                }
            } else {
                setValue(castValue);
            }
            setInputValue(castValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.option]);

        const saveOptionValue = (newValue: number | number[]) => {
            if (!props.option) return;

            if (isSliderArray(props.option)) {
                const [opt1, opt2] = props.option;
                const updatedOption: ISliderInterface = {
                    key: opt1!.key,
                    value: (newValue as number[])[0],
                };
                const updatedOption2: ISliderInterface = {
                    key: opt2!.key,
                    value: (newValue as number[])[1],
                };
                props.setOption([updatedOption, updatedOption2] as T)
                if (opt1 && opt1.value && opt1.value !== (newValue as number[])[0]) {
                    props.triggerOnFirstArrayValueChange?.()
                }
            } else {
                const updatedOption: ISliderInterface = {
                    key: props.option.key,
                    value: newValue as number,
                };

                props.setOption(updatedOption as T, props.basic);
            }
        };

        const handleSaveValue = (value: number | number[]) => {
            setInputValue(value);
        };

        const handleSliderCommittedChange = (value: number | number[]) => {
            setValue(value);
            setInputValue(value);
            saveOptionValue(value);
        };

        const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            let number = event.target.value === '' ? 0 : Number(event.target.value);
            if (props.lockMaxValue && number > props.maxValue) {
                number = props.maxValue;
            } else if (props.lockMinValue && number < props.minValue) {
                number = props.minValue;
            }

            setValue(number);
            setInputValue(number);
            saveOptionValue(number);
        };

        const handleIconClick = () => {
            const defOpt = props.defaultOption;

            if (Array.isArray(defOpt)) {
                if (defOpt === undefined || defOpt[0]?.value === undefined || defOpt[1]?.value === undefined) {
                    enqueueSnackbar("Could not get default value for range slider", { variant: "error" });
                } else {
                    setValue([defOpt[0]?.value, defOpt[1]?.value]);
                    setInputValue([defOpt[0]?.value, defOpt[1]?.value]);
                    saveOptionValue([defOpt[0]?.value, defOpt[1]?.value]);
                }
            } else if (defOpt && (defOpt.value || defOpt.value === 0)) {
                const defVal = Number(defOpt.value);
                setValue(defVal);
                setInputValue(defVal);
                saveOptionValue(defVal);
            } else {
                enqueueSnackbar("Could not get default value", { variant: "error" });
            }
        };

        const noWarningMessage = () => (
            <Typography className={classes.typographyNoWarning} align="center">
                {props.noWarningMsg ?? "Warning never shown"}
            </Typography>
        );

        const alwaysWarningMessage = () => (
            <Typography className={classes.typographyAlwaysWarning} align="center">
                {props.alwaysWarningMsg ?? "Warning always shown"}
            </Typography>
        );

        const badValueRangeMessage = () => (
            <Typography className={classes.typographyAlwaysWarning} align="center">
                {"Value not recommended"}
            </Typography>
        );

        const noMessage = () => (
            <Typography className={classes.typographyAlwaysWarning} align="center">
                &nbsp;
            </Typography>
        );

        const handleThresholdMessage = () => {
            if (props.type !== "static" && !props.dontShowWarningMsg) {
            if (props.type === "raising") {
                if (value === props.minValue) return noWarningMessage();
                if (value === props.maxValue) return alwaysWarningMessage();
            } else if (props.type === "falling") {
                if (value === props.minValue) return alwaysWarningMessage();
                if (value === props.maxValue) return noWarningMessage();
            }
            }
            if (props.badValueRange && (value as number) > props.badValueRange[0] && (value as number) < props.badValueRange[1]) {
                return badValueRangeMessage();
            }
            return noMessage();
        };

        return (
            <TableCell className={classes.tableCell} colSpan={props.colspan ?? 1} component="th">
                <Grid container>
                    <Grid item xs={12}>
                        <Typography component="span" className={props.active? classes.title : classes.titleDisabled}>
                            {props.title}
                            {props.info && (
                                <StyledTooltip disableFocusListener title={props.info}>
                                    <div style={{display: "inline"}}>
                                        <HelpOutlineIcon className={classes.iconInfo} />
                                    </div>
                                </StyledTooltip>
                            )}
                        </Typography>
                    </Grid>
                    <Grid container>
                        <Grid item xs={8} className={classes.gridSlider}>
                            <Slider
                                active={props.active}
                                minValue={props.minValue}
                                maxValue={props.maxValue}
                                steps={props.steps}
                                value={value}
                                saveValue={handleSaveValue}
                                commitValue={handleSliderCommittedChange}
                                valueLabel={props.valueLabel}
                                type={props.type}
                                lockFirstArrayNumber={props.lockFirstArrayNumber}
                            />
                            {handleThresholdMessage()}
                        </Grid>
                        <Grid item xs={2}>
                            {!isSliderArray(props.option) && (
                                <Input
                                    disabled={!props.active}
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    inputProps={{
                                        step: 1,
                                        min: props.minValue,
                                        max: props.type !== "infinite" ? props.maxValue : null,
                                        type: "number",
                                        "aria-labelledby": "input-slider",
                                    }}
                                    color="primary"
                                />
                            )}
                        </Grid>
                        <Grid item xs={2}>
                            <IconButton
                                onClick={handleIconClick}
                                color="primary"
                                disabled={!props.active}
                                className={props.active? classes.iconButton : classes.iconButtonDisabled}
                            >
                                <CachedIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
            </TableCell>
        );
    };

export default SliderCell;